import {
  AwaitContainer,
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Item,
  ItemActions,
  ItemHeader,
  ItemTitle,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserGroupListItem, useUserGroups } from '@/entities/userGroups';

type Props = {
  existingGroupIds: number[]; // Идентификаторы уже выбранных групп
  onCancel: () => void;
  onSave: (groups: UserGroupListItem[]) => void;
  className?: string;
};

export const AddUserGroupsDialog: FC<Props> = ({ existingGroupIds, onCancel, onSave, className }) => {
  const { t } = useTranslation(['common', 'users']);

  // Отдельный запрос для диалога (может иметь дополнительные параметры)
  // React Query автоматически использует кэш, если параметры совпадают
  const { data, isLoading } = useUserGroups({
    limit: 100,
  });

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // Исключаем уже выбранные группы
  const availableGroups: UserGroupListItem[] = useMemo(() => {
    return data?.userGroups?.filter((group) => !existingGroupIds.includes(group.id)) || [];
  }, [existingGroupIds, data?.userGroups]);

  // Локальная фильтрация по поисковому запросу
  const filtered = useMemo(() => {
    if (!search.trim()) return availableGroups;
    const q = search.toLowerCase();
    return availableGroups.filter((group) => {
      return group.name.toLowerCase().includes(q);
    });
  }, [availableGroups, search]);

  const handleSave = () => {
    // Передаем только выбранные группы
    onSave(availableGroups.filter((group) => selectedIds.includes(group.id)));
  };

  return (
    <DialogContent className={className}>
      <DialogHeader>
        <DialogTitle>{t('users:addGroup', { defaultValue: 'Add group' })}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common:search')} />

        <AwaitContainer isLoading={isLoading}>
          <div className="flex flex-col gap-3 max-h-120 overflow-auto">
            {filtered.map((group) => (
              <Item variant="outline" size="sm" key={group.id}>
                <ItemHeader>
                  <ItemTitle>{group.name}</ItemTitle>
                  <ItemActions className="ml-4">
                    <Checkbox value={selectedIds.includes(group.id)} onClick={() => handleToggle(group.id)} />
                  </ItemActions>
                </ItemHeader>
              </Item>
            ))}
            {filtered.length === 0 && <div className="text-muted-foreground">{t('users:notFound')}</div>}
          </div>
        </AwaitContainer>
      </div>

      <DialogFooter>
        <div className="flex gap-3 justify-end w-full">
          <Button variant="ghost" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSave} disabled={selectedIds.length === 0}>
            {t('common:add')}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
